import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ArrowLeft, Printer, Buildings, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = (fid) => `trf-poster-edits-${fid}`;

const DEFAULTS = {
  headline: "Report a Maintenance Issue",
  subheadline: "Quick, simple, no app required.",
  cta: "Scan the QR or visit the link",
  bullets: ["Critical (gas / leak / electric) is logged for emergency dispatch",
            "Photos & videos welcome",
            "Live status updates by WhatsApp"],
  contact_label: "In an emergency outside this service:",
  contact_value: "Always call 999 if there is risk to life.",
  show_logo: true,
};

export default function TenantReportFormPosterPage() {
  const { fid } = useParams();
  const [form, setForm] = useState(null);
  const [edits, setEdits] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await API.get(`/tenant-report-forms/${fid}`);
      setForm(res.data);
      // Restore previous poster edits for this form
      try {
        const saved = JSON.parse(localStorage.getItem(LS_KEY(fid)) || "null");
        if (saved) setEdits({ ...DEFAULTS, ...saved });
      } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load form");
    } finally {
      setLoading(false);
    }
  }, [fid]);

  useEffect(() => { load(); }, [load]);

  const updateField = (k, v) => {
    const next = { ...edits, [k]: v };
    setEdits(next);
    try { localStorage.setItem(LS_KEY(fid), JSON.stringify(next)); } catch (e) { /* ignore */ }
  };

  const updateBullet = (i, v) => {
    const bullets = [...edits.bullets];
    bullets[i] = v;
    updateField("bullets", bullets);
  };

  const addBullet = () => updateField("bullets", [...edits.bullets, ""]);
  const removeBullet = (i) => updateField("bullets", edits.bullets.filter((_, idx) => idx !== i));

  const resetEdits = () => {
    localStorage.removeItem(LS_KEY(fid));
    setEdits(DEFAULTS);
    toast.success("Reset to defaults");
  };

  const downloadPdf = async () => {
    try {
      const res = await API.post(
        `/tenant-report-forms/${fid}/poster-pdf`,
        edits,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (form?.name || "tenant-report").replace(/\s+/g, "_").slice(0, 40);
      a.download = `${safe}_poster.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "PDF generation failed");
    }
  };

  if (loading || !form) return <div className="p-8 text-center text-slate-400">Loading…</div>;

  const accent = form.branding?.accent_color || "#1D4ED8";
  const publicUrl = `${window.location.origin}/report-issue/${form.token}`;
  const logo = form.branding?.logo_url || "";

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 print:bg-white" data-testid="trf-poster-page">
      {/* Print-only styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          .no-print { display: none !important; }
          .poster-page { box-shadow: none !important; margin: 0 !important; }
        }
      `}</style>

      {/* Top toolbar (hidden on print) */}
      <div className="no-print sticky top-0 z-10 bg-card border-b border-border px-6 py-3 flex items-center gap-3 flex-wrap">
        <Link to="/tenant-report-forms" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <span className="text-slate-400">/</span>
        <p className="text-sm font-medium truncate flex-1">Print poster — {form.name}</p>
        <Button size="sm" variant="outline" onClick={resetEdits} data-testid="trf-poster-reset">Reset</Button>
        <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="trf-poster-download-pdf">
          <FilePdf className="h-3.5 w-3.5 mr-1" /> Save as PDF
        </Button>
        <Button size="sm" onClick={() => window.print()} data-testid="trf-poster-print">
          <Printer className="h-3.5 w-3.5 mr-1" /> Print
        </Button>
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-6 p-6 max-w-[1400px] mx-auto">
        {/* Editor (hidden on print) */}
        <div className="no-print bg-card border border-border rounded-xl p-5 space-y-4 self-start lg:sticky lg:top-20">
          <h2 className="text-lg font-semibold">Edit poster</h2>
          <p className="text-xs text-slate-500">Edits are saved locally — only the QR and brand colour come from the form's settings.</p>

          <div>
            <Label>Headline</Label>
            <Input value={edits.headline} onChange={(e) => updateField("headline", e.target.value)}
                   className="mt-1" data-testid="trf-poster-headline" />
          </div>
          <div>
            <Label>Sub-headline</Label>
            <Input value={edits.subheadline} onChange={(e) => updateField("subheadline", e.target.value)}
                   className="mt-1" data-testid="trf-poster-subheadline" />
          </div>
          <div>
            <Label>Call-to-action</Label>
            <Input value={edits.cta} onChange={(e) => updateField("cta", e.target.value)}
                   className="mt-1" data-testid="trf-poster-cta" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Bullet points</Label>
              <button onClick={addBullet} className="text-xs text-primary hover:underline" data-testid="trf-poster-bullet-add">+ Add</button>
            </div>
            {edits.bullets.map((b, i) => (
              <div key={i} className="flex gap-2">
                <Input value={b} onChange={(e) => updateBullet(i, e.target.value)} className="text-sm" data-testid={`trf-poster-bullet-${i}`} />
                <button onClick={() => removeBullet(i)} className="text-red-500 hover:text-red-700 px-2 text-xs">Remove</button>
              </div>
            ))}
          </div>

          <div>
            <Label>Footer label</Label>
            <Input value={edits.contact_label} onChange={(e) => updateField("contact_label", e.target.value)}
                   className="mt-1" data-testid="trf-poster-contact-label" />
          </div>
          <div>
            <Label>Footer value</Label>
            <Textarea value={edits.contact_value} onChange={(e) => updateField("contact_value", e.target.value)}
                      rows={2} className="mt-1 text-sm" data-testid="trf-poster-contact-value" />
          </div>

          <label className="flex items-center gap-2 text-xs cursor-pointer pt-1">
            <input type="checkbox" checked={edits.show_logo} onChange={(e) => updateField("show_logo", e.target.checked)} />
            Show logo in header (uses form's branding logo URL)
          </label>
        </div>

        {/* Poster preview / printable */}
        <div className="poster-page bg-white text-slate-900 rounded-xl shadow-lg overflow-hidden mx-auto w-full max-w-[210mm]" style={{ aspectRatio: "1 / 1.414" }} data-testid="trf-poster-preview">
          {/* Brand colour band */}
          <div className="h-3" style={{ backgroundColor: accent }} />

          {/* Header */}
          <div className="px-12 pt-10 flex items-start justify-between gap-6">
            <div>
              {edits.show_logo && logo ? (
                <img src={logo} alt="logo" className="max-h-14 mb-3" />
              ) : (
                <div className="h-12 w-12 rounded-md flex items-center justify-center mb-3" style={{ backgroundColor: accent }}>
                  <Buildings className="h-6 w-6 text-white" />
                </div>
              )}
              <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: accent }}>
                {form.client_company || "Maintenance Reports"}
              </p>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Powered by Fixlr</p>
          </div>

          {/* Headline */}
          <div className="px-12 pt-6">
            <h1 className="text-[44px] leading-[1.05] font-bold tracking-tight">{edits.headline}</h1>
            <p className="text-lg text-slate-600 mt-3">{edits.subheadline}</p>
          </div>

          {/* QR + URL */}
          <div className="px-12 pt-8 flex items-center gap-7">
            <img src={`${BACKEND}/api/tenant-report-forms/${fid}/qr`}
                 alt="QR code"
                 className="w-[200px] h-[200px] border-4 rounded-md"
                 style={{ borderColor: accent }} />
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: accent }}>
                {edits.cta}
              </p>
              <p className="text-base font-mono break-all bg-slate-100 rounded-md px-3 py-2.5">
                {publicUrl}
              </p>
              <p className="text-xs text-slate-500 mt-2">Or scan the QR with your phone camera.</p>
            </div>
          </div>

          {/* Bullets */}
          {edits.bullets.filter(Boolean).length > 0 && (
            <div className="px-12 pt-8">
              <ul className="space-y-2">
                {edits.bullets.filter(Boolean).map((b, i) => (
                  <li key={`${b}-${i}`} className="flex items-start gap-3 text-[15px]">
                    <span className="h-2 w-2 rounded-full mt-2 shrink-0" style={{ backgroundColor: accent }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer / contact */}
          {(edits.contact_label || edits.contact_value) && (
            <div className="px-12 mt-auto pt-10">
              <div className="border-t border-slate-200 pt-5">
                {edits.contact_label && (
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1">{edits.contact_label}</p>
                )}
                {edits.contact_value && (
                  <p className="text-sm text-slate-700 whitespace-pre-line">{edits.contact_value}</p>
                )}
              </div>
            </div>
          )}

          {/* Bottom brand band */}
          <div className="h-3 mt-10" style={{ backgroundColor: accent }} />
        </div>
      </div>
    </div>
  );
}
